import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, clearMsalCache } from '../config/msalConfig';
import { acquireApiToken } from '../auth/tokenAcquisition';

/**
 * Authentication hook wrapping MSAL. Mirrors the CustomerSuccessPortal pattern (login /
 * logout / token acquisition) but reads MSAL state directly via msal-react rather than a
 * separate store — the route guard only needs `isAuthenticated`.
 */
export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const loginRedirect = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  /** Sign in via redirect. Pass `{ prompt: 'select_account' }` to force the account picker —
      e.g. from the no-access screen so a user signed into the wrong identity can switch
      accounts (SPA-Auth Standard, rule 6) rather than being stuck. */
  const login = useCallback(
    async (options?: { prompt?: 'select_account' | 'login' | 'consent' | 'none' }) => {
      await instance.loginRedirect({ ...loginRequest, prompt: options?.prompt ?? loginRequest.prompt });
    },
    [instance],
  );

  const logoutRedirect = useCallback(async () => {
    await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  /** Acquire an API access token via the shared token path (initialize → silent → guarded
      interactive fallback on needs-interaction, incl. timed_out). Returns null when a redirect
      is in progress or acquisition failed. */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const active = instance.getActiveAccount() ?? accounts[0] ?? null;
    if (!active) return null;
    return acquireApiToken(instance, active);
  }, [instance, accounts]);

  const forceLogout = useCallback(() => {
    clearMsalCache();
    window.location.href = '/welcome';
  }, []);

  return {
    isAuthenticated,
    isReady: inProgress === InteractionStatus.None,
    account,
    displayName: account?.name ?? account?.username ?? null,
    login,
    loginRedirect,
    logoutRedirect,
    getAccessToken,
    forceLogout,
  };
}
